import {
  GoogleOauthCodeClient,
  GoogleOauthCodeResponse,
} from "../../domain/types/GoogleOauthCodeClient";

interface WindowWithGoogleScriptLoad extends Window {
  onGoogleScriptLoad?: () => void;
  google?: {
    accounts: {
      oauth2: {
        initCodeClient: (config: any) => GoogleOauthCodeClient;
      };
    };
  };
}

const windowWithGoogleScriptLoad =
  window as unknown as WindowWithGoogleScriptLoad;

export class GoogleAuth2Service {
  private static instance: GoogleAuth2Service;
  private client: GoogleOauthCodeClient | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  public static getInstance(): GoogleAuth2Service {
    if (!GoogleAuth2Service.instance) {
      GoogleAuth2Service.instance = new GoogleAuth2Service();
    }
    return GoogleAuth2Service.instance;
  }

  public async configureAuthClient(
    scopes: string[],
    callback: (response: GoogleOauthCodeResponse) => void,
    clientId: string,
    options?: {
      redirectUri?: string;
      state?: string;
      loginHint?: string;
      hd?: string;
      uxMode?: "popup" | "redirect";
      selectAccount?: boolean;
      accessType?: string;
      prompt?: string;
      errorCallback?: (error: {
        type: "popup_failed_to_open" | "popup_closed" | "unknown";
      }) => void;
    },
  ): Promise<void> {
    await this.loadGoogleSdk();

    const config = {
      client_id: clientId,
      scope: scopes.join(" "),
      callback: callback,
      redirect_uri: options?.redirectUri,
      state: options?.state,
      login_hint: options?.loginHint,
      hd: options?.hd,
      ux_mode: options?.uxMode || "popup",
      select_account: options?.selectAccount || false,
      access_type: options?.accessType,
      prompt: options?.prompt,
      error_callback: options?.errorCallback,
    };

    if (!windowWithGoogleScriptLoad.google) {
      console.error("Google SDK not loaded.");
      throw new Error("Google SDK not loaded.");
    }

    this.client =
      windowWithGoogleScriptLoad.google.accounts.oauth2.initCodeClient(config);
  }

  public requestCode() {
    if (this.client) {
      this.client.requestCode();
    } else {
      console.error("Google Auth client not initialized");
    }
  }

  private loadGoogleSdk(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (windowWithGoogleScriptLoad.google) {
        resolve();
        return;
      }

      windowWithGoogleScriptLoad.onGoogleScriptLoad = () => {
        resolve();
      };

      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (windowWithGoogleScriptLoad.onGoogleScriptLoad) {
          windowWithGoogleScriptLoad.onGoogleScriptLoad();
        }
      };
      script.onerror = () => {
        reject(new Error("Failed to load the Google Identity Services SDK"));
      };
      document.head.appendChild(script);
    });
  }
}
